import {memo, useCallback, useRef, useState} from 'react'
import {SendHorizontal} from 'lucide-react'
import {cn} from '@/shared/lib/cn'

interface ChatInputProps {
  onSend: (message: string) => Promise<unknown>
  isSending: boolean
}

const textareaBaseClasses =
  'w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none resize-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] min-h-9 max-h-32 overflow-y-auto'

export const ChatInput = memo(function ChatInput({onSend, isSending}: ChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = useCallback(async () => {
    const trimmed = value.trim()
    if (!trimmed || isSending) return
    setValue('')
    await onSend(trimmed)
    textareaRef.current?.focus()
  }, [value, isSending, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void handleSend()
      }
    },
    [handleSend],
  )

  return (
    <div className="flex gap-2 p-3 border-t border-secondary shrink-0 items-end">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Написать сообщение..."
        disabled={isSending}
        rows={1}
        className={cn(
          textareaBaseClasses,
          'flex-1 bg-muted/50 border-input text-foreground placeholder:text-muted-foreground focus-visible:ring-ring/20 text-sm',
        )}
        autoComplete="off"
      />
      <button
        onClick={() => void handleSend()}
        disabled={!value.trim() || isSending}
        aria-label="Отправить сообщение"
        className={cn(
          'size-9 shrink-0 rounded-lg flex items-center justify-center transition-all duration-150',
          'bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:pointer-events-none',
          '[&_svg]:size-4 text-primary-foreground',
        )}
      >
        <SendHorizontal />
      </button>
    </div>
  )
})
